/**
 * 1.6.0 AI assistant access (plan Part A3; docs/dev/mcp-1.6.0-build.md): personal assistant tokens (`pmcp_`), the
 * workspace's assistant settings, what a token may do (`GET /api/v1/me/capabilities`), and the error codes of the whole
 * 1.6.0 release (tokens, the gateway, AI description). Stream T builds the routes and screens, M the program that uses
 * them, A the log-description permission.
 */
import { z } from 'zod';

/* ---------------------------------------------------------------- tokens */

/** `pmcp_` + 32 random bytes (base64url, 43 characters). Only the SHA-256 of the whole token is stored (hashToken). */
export const USER_TOKEN_PREFIX = 'pmcp_';
export const USER_TOKEN_BYTES = 32;
/** What is shown to identify a token: the prefix and this many characters after it (`pmcp_AbCdEfGh`). */
export const USER_TOKEN_DISPLAY_CHARS = 8;
export const USER_TOKEN_KINDS = ['mcp'] as const;
export type UserTokenKind = (typeof USER_TOKEN_KINDS)[number];
/** The expiry choices (days) when making a token; the workspace's maximum may leave out the longer ones. */
export const USER_TOKEN_EXPIRY_DAYS = [30, 90, 365] as const;
/** Requests per minute per token (RateLimiter); over it: 429 TOKEN_RATE_LIMITED with Retry-After. */
export const USER_TOKEN_RATE_PER_MINUTE = 120;
/** At most this many live (not revoked, not expired) tokens per person. */
export const USER_TOKEN_MAX_PER_USER = 10;

/** What `auditAs` records as detail.via for a request made with a token (AuthContext.via). */
export const USER_TOKEN_VIA = { readWrite: 'mcp', readOnly: 'mcp-read-only' } as const;

/** A token as its owner (My account) and administrators (Access → AI assistants) see it. Never the secret. */
export interface UserTokenDTO {
  id: string;
  name: string;
  kind: UserTokenKind;
  /** `pmcp_AbCdEfGh`, to recognise it. */
  prefix: string;
  readOnly: boolean;
  /** "Write log descriptions" (C1): write_segment may write suggestions. Off by default, separate from read-only. */
  writeLog: boolean;
  createdAt: string;
  expiresAt: string;
  lastUsedAt: string | null;
  lastUsedIp: string | null;
  uses: number;
  revokedAt: string | null;
  /** live: usable; expired; revoked. */
  state: 'live' | 'expired' | 'revoked';
}

/** An administrator's list (GET /api/v1/admin/user-tokens): every person's tokens, with whose. */
export interface AdminUserTokenDTO extends UserTokenDTO {
  user: { id: string; displayName: string; email: string; role: string; disabled: boolean };
}

export const CreateUserTokenInput = z.object({
  name: z.string().trim().min(1).max(80),
  expiresInDays: z.union([z.literal(30), z.literal(90), z.literal(365)]).default(90),
  readOnly: z.boolean().default(true),
  writeLog: z.boolean().default(false),
}).strict();
export type CreateUserTokenInput = z.infer<typeof CreateUserTokenInput>;

/** The answer to creating a token: the secret, shown once, with the setup command for the person's computer. */
export interface CreatedUserTokenDTO {
  token: UserTokenDTO;
  secret: string;
  /** `prism-mcp setup --url https://prism.example.com --token pmcp_…` */
  setupCommand: string;
}

/** Rename, or change read-only and Write log descriptions (only towards less: read-only on, writeLog off, unless allowed). */
export const UpdateUserTokenInput = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  readOnly: z.boolean().optional(),
  writeLog: z.boolean().optional(),
}).strict();
export type UpdateUserTokenInput = z.infer<typeof UpdateUserTokenInput>;

/* ---------------------------------------------------------------- workspace settings */

/** Administration → Access → AI assistants (workspace_settings.assistant_*, 0100). */
export interface AssistantSettingsDTO {
  /** "Allow AI assistant access": off by default. Off: every token is refused (TOKEN_INVALID is not said: ASSISTANT_ACCESS_OFF). */
  enabled: boolean;
  /** Roles whose members may create tokens: admin only by default (Mikael, 2026-10-25). */
  tokenRoles: string[];
  /** The longest expiry a new token may have, in days (1–365; default 365). */
  maxExpiryDays: number;
  /** Only read-only tokens may be made, and existing read-write tokens act read-only. */
  readOnlyOnly: boolean;
  /** Live tokens in the workspace, and when one was last used. */
  liveTokens: number;
  lastUsedAt: string | null;
  updatedAt: string | null;
}

export const AssistantSettingsInput = z.object({
  enabled: z.boolean(),
  tokenRoles: z.array(z.string().regex(/^[a-z0-9_]{1,40}$/)).max(50),
  maxExpiryDays: z.number().int().min(1).max(365),
  readOnlyOnly: z.boolean(),
}).strict();
export type AssistantSettingsInput = z.infer<typeof AssistantSettingsInput>;

export const ASSISTANT_SETTINGS_DEFAULTS: AssistantSettingsInput = { enabled: false, tokenRoles: ['admin'], maxExpiryDays: 365, readOnlyOnly: false };

/** My account → AI assistant access: whether the tab shows and what the person may do there. */
export interface MyAssistantAccessDTO {
  /** The workspace allows assistant access. */
  enabled: boolean;
  /** This person's role may create tokens. */
  canCreate: boolean;
  maxExpiryDays: number;
  readOnlyOnly: boolean;
  /** PRISM's public address, for the setup command. */
  publicUrl: string;
}

/* ---------------------------------------------------------------- capabilities */

/**
 * GET /api/v1/me/capabilities (any authenticated caller; the MCP program asks it first): who the caller is, how
 * (session, API key, assistant token), and which MCP tools would work, so the program offers only those. A tool is
 * listed when the caller has its permissions (MCP_TOOLS[name].permissions), the token is not read-only for a write
 * tool, and writeLog is on for write_segment.
 */
export interface CapabilitiesDTO {
  user: { id: string; displayName: string; email: string; role: string };
  workspace: { id: string; name: string };
  via: 'session' | 'api_key' | 'mcp';
  token: { id: string; name: string; readOnly: boolean; writeLog: boolean; expiresAt: string } | null;
  permissions: string[];
  /** MCP tool names (mcp.ts MCP_TOOL_NAMES) this caller may use. */
  tools: string[];
  /** PRISM's version (the program warns when it is older than itself). */
  version: string;
}

/* ---------------------------------------------------------------- errors (all of 1.6.0) */

/**
 * The 1.6.0 error codes and their HTTP statuses. Errors as `{error: {code, message, details?}}` (HttpError). Messages
 * here are the defaults; routes may say more. The MCP program maps them to short sentences for the assistant
 * (mcp.ts MCP_ERROR_TEXT).
 */
export const RELEASE_160_ERRORS = {
  // tokens (T)
  TOKEN_INVALID: { status: 401, message: 'The assistant token is unknown, revoked or expired' },
  ASSISTANT_ACCESS_OFF: { status: 403, message: 'AI assistant access is off in this workspace' },
  TOKEN_READ_ONLY: { status: 403, message: 'This assistant token is read-only' },
  TOKEN_SCOPE: { status: 403, message: 'Assistant tokens cannot use this part of PRISM' },
  TOKEN_RATE_LIMITED: { status: 429, message: 'Too many requests with this token; wait a moment' },
  TOKEN_ROLE_NOT_ALLOWED: { status: 403, message: 'Your role may not create assistant tokens' },
  TOKEN_EXPIRY_TOO_LONG: { status: 422, message: 'That expiry is longer than this workspace allows' },
  TOKEN_LIMIT: { status: 409, message: 'You have as many live tokens as allowed; revoke one first' },
  TOKEN_WRITE_LOG_REQUIRED: { status: 403, message: 'This token may not write log descriptions' },
  TOKEN_REVOKED: { status: 409, message: 'This token is revoked; make a new one' },
  READ_ONLY_ONLY: { status: 422, message: 'This workspace allows read-only assistant tokens only' },
  UNKNOWN_ROLE: { status: 422, message: 'No such role' },
  // the gateway (G, W)
  AGENT_NO_GATEWAY: { status: 409, message: 'Update the agent and enable its gateway' },
  GATEWAY_OFF: { status: 409, message: 'The agent’s API gateway is off' },
  GATEWAY_FILE_ERROR: { status: 409, message: 'The agent’s gateway.json was refused; see the agent’s report' },
  UNKNOWN_SITE_SERVICE: { status: 422, message: 'The agent does not report that site service' },
  SITE_SERVICE_METHOD: { status: 422, message: 'The site service does not allow that method' },
  SITE_SERVICE_PATH: { status: 422, message: 'The site service does not allow that path' },
  AGENT_OFFLINE: { status: 503, message: 'The storage agent is not connected' },
  SITE_SERVICE_TIMEOUT: { status: 504, message: 'The agent did not answer in time' },
  // AI description (A)
  SEGMENT_HUMAN: { status: 409, message: 'A person wrote or edited this segment; leave it as it is' },
  NOT_A_SUGGESTION: { status: 409, message: 'This is not a suggestion (it was accepted, or a person wrote it)' },
  SUGGESTION_NOT_FOUND: { status: 404, message: 'The suggestion is gone (accepted, rejected or replaced)' },
  MEDIA_LINKS_NOT_ALLOWED: { status: 422, message: 'This webhook endpoint may not receive media links (Administration → Webhooks)' },
  MEDIA_LINK_EXPIRED: { status: 410, message: 'The media link has expired' },
  BODY_JSON_INVALID: { status: 422, message: 'The JSON body is not valid' },
  SHOT_NOT_FOUND: { status: 404, message: 'The asset has no shot with that number' },
  FRAME_FAILED: { status: 502, message: 'The frame could not be taken from the proxy' },
  NO_SHOTS: { status: 409, message: 'No shots have been detected on this asset yet; run Detect shots first' },
  MEDIA_LINK_INVALID: { status: 422, message: 'A media link function was given an argument it cannot use' },
} as const;
export type Release160ErrorCode = keyof typeof RELEASE_160_ERRORS;
